import * as React from 'react'
import Popover from '@mui/material/Popover'
import { useTheme } from '@mui/material/styles'
import { ActivityStatus } from '@user/features/Activity/constants'
import type { CalendarEvent } from '../../typings'
import { resolveEventColor } from '../../eventColors'
import { useEventDetail } from '../../hooks/useEventDetail'
import { PopoverActions } from './PopoverActions'
import { PopoverHead } from './PopoverHead'
import { PopoverBody } from './PopoverBody'
import { PopoverFooter } from './PopoverFooter'

type Props = {
  event: CalendarEvent
  anchorEl: HTMLElement
  open: boolean
  onClose: () => void
  /** Reports a status the user committed, so the page can keep showing it
   *  when the popover is reopened before the feed refreshes. */
  onStatusCommitted?: (status: ActivityStatus) => void
  /** SH-1432: open the Edit modal / delete confirmation for this event. Wired
   *  by CalendarPage; gated per-event by the server-derived permissions below. */
  onEdit?: (event: CalendarEvent) => void
  onDelete?: (event: CalendarEvent) => void
}

// Chip label per SC sub-type (SPEC-EventDetailPopover §4). Distinct from the
// filter-chip labels ("Activity Reminders"): the popover header reads "Activity".
const CHIP_LABEL: Record<string, string> = {
  activity_reminder: 'Activity',
  next_action_date: 'Next Action Date',
  expected_close_date: 'Expected Close Date',
}

const MARKER_SUBTYPES = new Set(['next_action_date', 'expected_close_date'])

const KNOWN_STATUSES = new Set<string>(Object.values(ActivityStatus))

/**
 * The event detail popover (SH-1986). Anchors to the clicked tile and opens to
 * its right (SPEC-EventDetailPopover). One component, two SC shapes — an
 * Activity and a marker (Next Action / Expected Close) — differing only by which
 * rows exist; a row with no data is removed from the DOM, never blanked (Q3).
 * PestPac/Google variants are out of scope (no feed yet). The four regions
 * (actions / head / body / footer) are their own components to stay readable.
 */
export function EventDetailPopover({
  event,
  anchorEl,
  open,
  onClose,
  onStatusCommitted,
  onEdit,
  onDelete,
}: Props) {
  const theme = useTheme()
  const color = resolveEventColor(event, theme)
  const detail = useEventDetail(open ? event : null)

  const isMarker = event.subType ? MARKER_SUBTYPES.has(event.subType) : false
  // subType is a closed union the mapper only ever sets to a CHIP_LABEL key, so
  // the fallback is belt-and-braces — but an unlabelled chip would also read as
  // "undefined: <title>" to a screen reader, which is worth one ?? here.
  const chipLabel = (event.subType && CHIP_LABEL[event.subType]) ?? 'Event'
  const dueLabel =
    event.subType === 'next_action_date'
      ? 'Next Action'
      : event.subType === 'expected_close_date'
        ? 'Expected Close'
        : 'Due'

  const title = isMarker
    ? (detail.data?.primaryContactName ?? event.title)
    : event.title

  // Status editing needs BOTH a status we can render and the id of the record
  // to write to, so the two travel as one value: the chip renders iff this is
  // defined, and it cannot be defined without an activity id.
  //
  // That id is the feed's explicit `activityId` and nothing else. Deriving it
  // from `event.id` would be a guess about the SH-1419 contract that fails
  // silently — a PUT to whatever record happens to share that number. Gating on
  // the field fails safe instead: until the feed sends it the chip is simply
  // not rendered, which is visible rather than wrong. An unrecognised status (a
  // future/renamed value) is likewise hidden rather than crashing the card.
  const editableStatus =
    !isMarker &&
    event.status != null &&
    KNOWN_STATUSES.has(event.status) &&
    event.activityId != null &&
    Number.isFinite(event.activityId)
      ? {
          activityId: event.activityId,
          value: event.status as ActivityStatus,
        }
      : undefined

  return (
    // role/aria-label match the tiles' aria-haspopup="dialog" and name the
    // popover for screen readers; MUI restores focus to the anchor tile on close.
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      marginThreshold={16}
      PaperProps={{
        'data-test-id': 'event-detail-popover',
        role: 'dialog',
        'aria-label': `${chipLabel}: ${title}`,
        sx: {
          width: 390,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          borderRadius: '12px',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <PopoverActions
        showEditDelete={!isMarker}
        // Edit/Delete act on an activity. An owner-visible SC event that is not an
        // activity (a plain calendar event) carries no activityId and is
        // permission-wise editable, so without this gate the icons would render
        // and the click would silently do nothing.
        canEdit={
          !isMarker && event.activityId != null && !!event.permissions?.canEdit
        }
        canDelete={
          !isMarker &&
          event.activityId != null &&
          !!event.permissions?.canDelete
        }
        onEdit={onEdit ? () => onEdit(event) : undefined}
        onDelete={onDelete ? () => onDelete(event) : undefined}
        onClose={onClose}
      />
      <PopoverHead
        event={event}
        isMarker={isMarker}
        title={title}
        chipLabel={chipLabel}
        chipColor={color.border}
        editableStatus={editableStatus}
        onStatusCommitted={onStatusCommitted}
      />
      <PopoverBody
        event={event}
        isMarker={isMarker}
        dueLabel={dueLabel}
        detail={detail}
      />
      <PopoverFooter deepLink={event.deepLink} />
    </Popover>
  )
}
